"use client";

import { useFormContext } from "react-hook-form";
import React, { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { File as FileIcon, FileText as PdfIcon, X } from "lucide-react"; // Import PDF icon
import { useLocale } from "use-intl";
import { translations } from "@/messages";
type FileUploadInputProps = {
  elementId: string;
  multiple?: boolean;
  disabled?: boolean;
  labelText?: string;
};
function FileUploadInput({
  elementId,
  multiple = false,
  disabled = false,
  labelText
}: FileUploadInputProps) {
  const locale = useLocale() as keyof typeof translations;
  const form = useFormContext();
  const [files, setFiles] = useState<File[]>([]);
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = multiple ? acceptedFiles : acceptedFiles.slice(-1);
    setFiles(prev => multiple ? [...prev, ...newFiles] : newFiles);
    form.setValue(elementId, multiple ? newFiles : newFiles[0]);
  }, [form, elementId, multiple]);
  const removeFile = (index: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevents opening the file input window
    setFiles(files.filter((_, idx) => idx !== index));
    form.setValue(elementId, multiple ? files.filter((_, idx) => idx !== index) : null);
  };
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    onDrop,
    disabled
  });
  return <div data-sentry-component="FileUploadInput" data-sentry-source-file="FileUploadInput.tsx">
      {labelText && <p className="mb-4 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {labelText}
        </p>}

      <div {...getRootProps()} className={`${!disabled ? "cursor-pointer" : ""} rounded-lg border-2 border-dashed border-gray-200 p-4 text-center`}>
        <input {...getInputProps()} disabled={disabled} multiple={multiple} />
        <div className={`flex flex-col items-center justify-center rounded-lg bg-glow/10 p-6`}>
          <div>
            <p className="text-glow">
              {multiple ? translations[locale].misc.dragAndDropMultiple : translations[locale].misc.dragAndDropSingle}
            </p>
          </div>
        </div>
        {files.length > 0 && <div className="mt-4 grid select-none grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
            {files.map((file, index) => <div key={index} className="relative overflow-hidden rounded-lg border text-center">
                <button onClick={event => removeFile(index, event)} className="absolute right-0 top-0 m-1 rounded-full bg-gray-300/50 p-0.5 text-gray-800/90 transition hover:bg-gray-300/70 focus:bg-white/50 focus:outline-none focus:ring-2 focus:ring-red-500/20">
                  <X size={20} className="drop-shadow-2xl" />
                </button>

                {file.type.includes("application/pdf") ? <PdfIcon className="mx-auto h-32 w-full" /> : file.type.startsWith("image/") ?
          // eslint-disable-next-line @next/next/no-img-element, @shopify/jsx-no-hardcoded-content
          <img src={URL.createObjectURL(file)} alt={`File preview ${index}`} className="mx-auto h-32 w-full object-cover" /> : <FileIcon className="mx-auto h-32 w-full" />}
                <p className="truncate p-4 text-sm font-semibold text-gray-600">{file.name}</p>
              </div>)}
          </div>}
      </div>
    </div>;
}
export default FileUploadInput;